import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { EMAIL_REGEX_FOR_VALIDATION } from '@ai/common/src/constants/constants';
import ErrorPane from '@ai/common/src/pages/auth/ErrorPane';
import { PasswordField } from '@ai/common/src/pages/auth/PasswordField';
import { getUserStore } from '@ai/common/src/store/UserStore';

import { paths } from '@common/paths';
import { LoginWith } from './LoginWith';
import { useLocalizedValidationErrorMessages } from '@ai/common/src/hooks/useLocalizedValidationErrorMessages';
import { IUser, UserRole } from '@ai/common/src/types/user';
import { useCallback } from 'react';

interface ILoginValues {
  email: string;
  password: string;
  submit: null;
}

const loginInitialValues: ILoginValues = {
  email: '',
  password: '',
  submit: null,
};

export const LoginForm = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { error, setError, loginWithCredentials } = getUserStore('platform');

  const loginValidationSchema = Yup.object({
    email: Yup.string()
      .trim()
      .matches(EMAIL_REGEX_FOR_VALIDATION, t('login.error.wrongEmail'))
      .max(255, t('validation.maxLength', { length: 255 }))
      .required(t('login.required.email')),
    password: Yup.string().trim().max(255).required(t('login.required.password')),
  });

  const navigateAfterLogin = useCallback(
    (currentUser: IUser, hasBalance: boolean) => {
      let targetRoute;

      switch (true) {
        case currentUser.role === UserRole.ADMIN:
          targetRoute = paths.administration.users;
          break;

        case currentUser.role === UserRole.DEFAULT_EXPERTS_MANAGER:
          targetRoute = paths.expertList.index;
          break;

        case !hasBalance:
          targetRoute = paths.pricing.index;
          break;

        case !!location.state?.from:
          targetRoute = location.state.from.substring(1);
          break;

        default:
          targetRoute = paths.home.index;
      }

      navigate(targetRoute);
    },
    [location.state?.from, navigate]
  );

  const form = useFormik({
    initialValues: loginInitialValues,
    validationSchema: loginValidationSchema,
    onSubmit: async (data: ILoginValues) => {
      try {
        const { currentUser, hasBalance } = await loginWithCredentials(
          data.email.trim(),
          data.password.trim()
        );
        navigateAfterLogin(currentUser, hasBalance);
      } catch (error) {
        console.error(error);
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const { handleSubmit, values, errors, isSubmitting, touched, handleChange } = form;

  useLocalizedValidationErrorMessages(form);

  return (
    <Stack width={'26rem'} gap={2}>
      <Stack sx={{ mb: 2 }} spacing={1}>
        <Typography fontSize={'2em'}>{t('login.title')}</Typography>

        <Box />

        <Stack direction="row" spacing={1}>
          <Typography color="text.secondary" variant="body2">
            {t('login.registerQuestion')}&nbsp;
          </Typography>
          <Link to={paths.auth.signup} style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="secondary">
              {t('login.register')}
            </Typography>
          </Link>
        </Stack>
      </Stack>
      <form noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            autoFocus
            error={!!(touched.email && errors.email)}
            fullWidth
            helperText={touched.email && errors.email}
            label={t('login.email')}
            id="email"
            name="email"
            type="email"
            variant="outlined"
            onChange={handleChange}
            onFocus={() => setError(undefined)}
            value={values.email}
            disabled={isSubmitting}
          />

          <PasswordField
            id="password"
            value={values.password}
            onChange={handleChange}
            error={!!(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            disabled={isSubmitting}
            label={t('login.password')}
            onFocus={() => setError(undefined)}
          />

          <ErrorPane error={error} />
        </Stack>

        <Link to={paths.auth.resetPassword} style={{ textDecoration: 'none' }}>
          <Typography variant="body2" color="secondary" sx={{ marginTop: 1 }}>
            {t('login.forgotPassword')}
          </Typography>
        </Link>

        <Button
          fullWidth
          sx={{ mt: 2 }}
          size="large"
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {t('login.signin')}
        </Button>
      </form>

      <LoginWith navigateAfterLogin={navigateAfterLogin} />
    </Stack>
  );
});
